import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../contexts/AppContext';
import { Sun, Moon, Globe, Phone, ShieldCheck, TrendingUp, Lock, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const HERO_IMAGE =
  'https://images.unsplash.com/photo-1530569673472-307dc017a82d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHszfHxzY2VuaWMlMjBsYW5kc2NhcGUlMjBuYXR1cmUlMjBtb3VudGFpbiUyMGZvZyUyMG1pc3QlMjBtaW5pbWFsfGVufDB8fHx8MTc5MDI2Njc0NHww&ixlib=rb-4.1.0&q=85';

/* ── Floating particles ─────────────────────────────────── */
const PARTICLE_COLORS = [
  'rgba(56,189,248,0.55)',
  'rgba(99,102,241,0.45)',
  'rgba(14,165,233,0.50)',
  'rgba(56,189,248,0.35)',
];
const particles = Array.from({ length: 14 }, (_, i) => ({
  size: 3 + (i % 4) * 2,
  color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
  left: `${3 + (i * 7) % 94}%`,
  top: `${5 + (i * 11) % 90}%`,
  duration: `${3.4 + (i % 5) * 0.65}s`,
  delay: `${i * 0.22}s`,
}));

/* ── Mini bar chart for floating card ───────────────────── */
const BARS = [3, 5, 4, 7, 5, 6, 4, 7];

export default function LoginPage() {
  const { login, toggleTheme, toggleLanguage, theme, language, t } = useApp();

  const [step, setStep]       = useState(1);
  const [mobile, setMobile]   = useState('');
  const [otp, setOtp]         = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [mockOtp, setMockOtp] = useState('');
  const [userType, setUserType] = useState('');
  const [logoSettings, setLogoSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(r => setLogoSettings(r.data)).catch(() => {});
  }, []);

  /* ── handlers ── */
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!mobile || mobile.length < 10) { setError('Please enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/send-otp`, { mobile_no: mobile });
      setUserType(res.data.user_type);
      if (res.data.mock_mode && res.data.otp) setMockOtp(res.data.otp);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp || otp.length < 4) { setError('Please enter the OTP'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { mobile_no: mobile, otp });
      login(res.data.token, res.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const logoSizeClass =
    logoSettings?.logo_size === 'small' ? 'h-8'
    : logoSettings?.logo_size === 'large' ? 'h-16'
    : 'h-12';
  const ldgTxtSzMap = { xs:'text-xs', sm:'text-sm', base:'text-base', lg:'text-lg', xl:'text-xl' };
  const landingTxtClass = ldgTxtSzMap[logoSettings?.logo_text_size || 'base'] || 'text-base';
  const landingTxtColor = logoSettings?.logo_text_color_landing || '#ffffff';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background" data-testid="login-page">

      {/* ══ LEFT — hero image ════════════════════════════════ */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src={HERO_IMAGE} alt="hero" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        <div className="relative z-10 flex flex-col justify-between p-10 text-white w-full">
          <div>
            {logoSettings && (
              logoSettings.landing_logo_url ? (
                <div className="flex items-center gap-2">
                  <img src={logoSettings.landing_logo_url} alt="logo"
                    className={`${logoSizeClass} object-contain flex-shrink-0`} />
                  {logoSettings.app_name && (
                    <span className={`font-semibold ${landingTxtClass} tracking-tight`}
                      style={{ color: landingTxtColor }}>
                      {logoSettings.app_name}
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                    <ShieldCheck size={18} />
                  </div>
                  <span className={`font-semibold ${landingTxtClass} tracking-tight`}
                    style={{ color: landingTxtColor }}>
                    {logoSettings.app_name || 'Credit Platform'}
                  </span>
                </div>
              )
            )}
          </div>
          <div>
            <h1 className="text-4xl font-bold mb-3 leading-tight">
              Secure Credit<br />Intelligence
            </h1>
            <p className="text-white/70 text-base">
              Access comprehensive credit reports with real-time data and intelligent analytics.
            </p>
          </div>
        </div>
      </div>

      {/* ══ RIGHT — animated login panel ════════════════════ */}
      <div className="flex-1 lg:w-1/2 flex flex-col min-h-screen bg-white dark:bg-background relative overflow-hidden">

        {/* — animated gradient orbs — */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="login-orb-1 absolute -top-28 -right-28 w-96 h-96 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.18) 0%, transparent 68%)' }}
          />
          <div
            className="login-orb-2 absolute -bottom-28 right-1/4 w-80 h-80 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(14,165,233,0.13) 0%, transparent 68%)' }}
          />
          <div
            className="login-orb-3 absolute top-1/2 -left-20 w-64 h-64 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 68%)' }}
          />
        </div>

        {/* — floating particles — */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {particles.map((p, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: p.size,
                height: p.size,
                backgroundColor: p.color,
                left: p.left,
                top: p.top,
                animation: `login-particle ${p.duration} ease-in-out ${p.delay} infinite`,
              }}
            />
          ))}
        </div>

        {/* — floating info card: credit score — */}
        <div
          className="absolute top-20 right-6 pointer-events-none hidden xl:block z-10"
          style={{ animation: 'card-bob 4.2s ease-in-out infinite' }}
        >
          <div className="bg-white/95 dark:bg-card/95 backdrop-blur-sm rounded-2xl p-4 shadow-2xl border border-sky-100 dark:border-border w-44">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <TrendingUp size={12} className="text-emerald-600" />
              </div>
              <span className="text-xs text-slate-400 dark:text-muted-foreground font-medium">Credit Score</span>
            </div>
            <p className="text-2xl font-extrabold text-slate-800 dark:text-foreground">742</p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-emerald-500 font-semibold">Excellent</span>
            </div>
          </div>
        </div>

        {/* — floating info card: security — */}
        <div
          className="absolute bottom-36 left-5 pointer-events-none hidden xl:block z-10"
          style={{ animation: 'card-bob-alt 5.1s ease-in-out 1.3s infinite' }}
        >
          <div className="bg-white/95 dark:bg-card/95 backdrop-blur-sm rounded-2xl p-4 shadow-2xl border border-sky-100 dark:border-border w-42">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                <Lock size={11} className="text-primary" />
              </div>
              <span className="text-xs text-slate-400 dark:text-muted-foreground font-medium">Secure</span>
            </div>
            <p className="text-sm font-bold text-slate-800 dark:text-foreground">256-bit SSL</p>
            <div className="flex items-end gap-0.5 mt-2 h-6">
              {BARS.map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm bg-primary/40 bar-pulse"
                  style={{
                    height: `${h * 3}px`,
                    animationDelay: `${i * 0.15}s`,
                    animationDuration: `${1.4 + i * 0.1}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* — floating badge — */}
        <div
          className="absolute top-1/2 right-8 pointer-events-none hidden 2xl:flex items-center gap-2 z-10"
          style={{ animation: 'badge-float 3.5s ease-in-out 0.8s infinite' }}
        >
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-full shadow-md">
            <Star size={11} className="text-amber-500 fill-amber-400" />
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">Trusted Platform</span>
          </div>
        </div>

        {/* ── top controls ─────────────────────────────────── */}
        <div className="relative z-20 flex items-center justify-between p-4 lg:p-6">
          {/* mobile logo */}
          <div className="flex lg:hidden items-center gap-2">
            {logoSettings && (logoSettings.landing_logo_url ? (
              <img src={logoSettings.landing_logo_url} alt="logo" className="h-8 object-contain" />
            ) : (
              <div className="flex items-center gap-1.5">
                <ShieldCheck size={16} className="text-primary" />
                <span className="font-semibold text-sm text-slate-800 dark:text-foreground">
                  {logoSettings.app_name || 'Credit Platform'}
                </span>
              </div>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="language-toggle-btn"
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                         border border-slate-200 dark:border-border
                         text-slate-600 dark:text-muted-foreground
                         hover:bg-slate-100 dark:hover:bg-secondary
                         transition-all"
            >
              <Globe size={13} />
              {language === 'en' ? 'தமிழ்' : 'EN'}
            </button>
            <button
              data-testid="theme-toggle-btn"
              onClick={toggleTheme}
              className="p-2 rounded-lg border border-slate-200 dark:border-border
                         text-slate-600 dark:text-muted-foreground
                         hover:bg-slate-100 dark:hover:bg-secondary
                         transition-all"
            >
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          </div>
        </div>

        {/* ── form area ────────────────────────────────────── */}
        <div className="relative z-20 flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-sm">

            {/* heading — staggered entrance */}
            <div className="mb-8" style={{ animation: 'fadeInUp 0.45s ease-out 0.05s both' }}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-foreground mb-2">
                {t('welcomeBack')}
              </h2>
              <p className="text-slate-500 dark:text-muted-foreground text-sm">
                {t('loginSubtitle')}
              </p>
            </div>

            {step === 1 ? (
              <form onSubmit={handleSendOtp} className="space-y-4">

                {/* mobile input — flex group (no overlay possible) */}
                <div style={{ animation: 'fadeInUp 0.45s ease-out 0.15s both' }}>
                  <label className="block text-sm font-medium text-slate-700 dark:text-foreground mb-1.5">
                    {t('mobileNumber')}
                  </label>
                  <div
                    className="flex items-center rounded-lg border border-slate-200 dark:border-border
                               bg-slate-50 dark:bg-input overflow-hidden
                               focus-within:ring-2 focus-within:ring-primary/40
                               focus-within:border-primary transition-all duration-200"
                  >
                    {/* prefix */}
                    <div className="flex items-center gap-1 px-3 py-2.5 border-r border-slate-200 dark:border-border bg-slate-100 dark:bg-secondary/50 flex-shrink-0">
                      <Phone size={13} className="text-slate-400 dark:text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-semibold text-slate-600 dark:text-muted-foreground select-none">
                        +91
                      </span>
                    </div>
                    {/* actual text input — no overlap possible */}
                    <input
                      data-testid="mobile-input"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={mobile}
                      onChange={e => setMobile(e.target.value.replace(/\D/g, ''))}
                      placeholder="10-digit mobile number"
                      className="flex-1 px-3 py-2.5 bg-transparent text-sm
                                 text-slate-800 dark:text-foreground
                                 placeholder:text-slate-400 dark:placeholder:text-muted-foreground
                                 focus:outline-none min-w-0"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div
                    data-testid="login-error"
                    className="px-3 py-2 bg-red-50 dark:bg-destructive/10
                               text-red-600 dark:text-destructive text-sm
                               rounded-lg border border-red-200 dark:border-destructive/20"
                    style={{ animation: 'fadeInUp 0.3s ease-out both' }}
                  >
                    {error}
                  </div>
                )}

                <div style={{ animation: 'fadeInUp 0.45s ease-out 0.25s both' }}>
                  <button
                    data-testid="send-otp-btn"
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full py-3 text-base
                               shadow-md shadow-primary/20
                               hover:shadow-lg hover:shadow-primary/30
                               hover:-translate-y-0.5 active:translate-y-0
                               transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Sending…
                      </span>
                    ) : t('sendOtp')}
                  </button>
                </div>
              </form>

            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">

                <div
                  className="p-3 bg-primary/8 dark:bg-primary/10 border border-primary/20 rounded-lg text-sm"
                  style={{ animation: 'fadeInUp 0.35s ease-out 0.05s both' }}
                >
                  <span className="font-semibold text-slate-800 dark:text-foreground">Verification code sent</span>
                  <p className="text-xs text-slate-500 dark:text-muted-foreground mt-1">
                    Check your registered email address for the code
                  </p>
                </div>

                {mockOtp && (
                  <div
                    data-testid="mock-otp-display"
                    className="p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg"
                    style={{ animation: 'fadeInUp 0.35s ease-out 0.1s both' }}
                  >
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                      {t('mockModeAlert')}
                    </p>
                    <p className="text-xl font-mono font-bold text-amber-600 dark:text-amber-400 mt-1 tracking-widest">
                      {mockOtp}
                    </p>
                  </div>
                )}

                <div style={{ animation: 'fadeInUp 0.45s ease-out 0.15s both' }}>
                  <label className="block text-sm font-medium text-slate-700 dark:text-foreground mb-1.5">
                    {t('enterOtp')}
                  </label>
                  <input
                    data-testid="otp-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="· · · · · ·"
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 dark:border-border
                               bg-slate-50 dark:bg-input
                               text-slate-800 dark:text-foreground
                               placeholder:text-slate-300 dark:placeholder:text-muted-foreground
                               text-center text-2xl tracking-[0.6em] font-mono font-bold
                               focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary
                               transition-all duration-200"
                    required
                  />
                </div>

                {error && (
                  <div
                    data-testid="otp-error"
                    className="px-3 py-2 bg-red-50 dark:bg-destructive/10
                               text-red-600 dark:text-destructive text-sm
                               rounded-lg border border-red-200 dark:border-destructive/20"
                  >
                    {error}
                  </div>
                )}

                <div style={{ animation: 'fadeInUp 0.45s ease-out 0.25s both' }}>
                  <button
                    data-testid="verify-otp-btn"
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full py-3 text-base
                               shadow-md shadow-primary/20
                               hover:shadow-lg hover:shadow-primary/30
                               hover:-translate-y-0.5 active:translate-y-0
                               transition-all duration-200 disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Verifying…
                      </span>
                    ) : t('verifyOtp')}
                  </button>
                </div>

                <div style={{ animation: 'fadeInUp 0.45s ease-out 0.3s both' }}>
                  <button
                    type="button"
                    onClick={() => { setStep(1); setOtp(''); setMockOtp(''); setError(''); }}
                    className="w-full py-2.5 px-4 rounded-lg border border-slate-200 dark:border-border
                               text-slate-600 dark:text-muted-foreground text-sm font-medium
                               hover:bg-slate-100 dark:hover:bg-secondary
                               transition-all duration-200"
                  >
                    {t('back')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
